import type { ReactNode } from 'react';

export interface DataTableColumn<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  width?: string;
}

export interface DataTableProps<T> {
  columns: Array<DataTableColumn<T>>;
  rows: T[];
  emptyState?: ReactNode;
  onRowClick?: (row: T) => void;
  rowId: (row: T, index: number) => string | number;
  selectedRowId?: string | number | null;
}

/**
 * Minimal table abstraction to keep markup readable. It intentionally avoids
 * advanced features (sorting/virtualization) until we understand the dataset
 * cardinality in production.
 */
const DataTable = <T,>({ columns, rows, emptyState, onRowClick, rowId, selectedRowId }: DataTableProps<T>) => {
  if (rows.length === 0) {
    return <div className="data-table__empty">{emptyState ?? <p>No records available.</p>}</div>;
  }

  return (
    <div className="data-table__container">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} style={column.width ? { width: column.width } : undefined}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const id = rowId(row, index);
            const isSelected = selectedRowId != null && selectedRowId === id;
            const className = [
              onRowClick ? 'data-table__row--actionable' : '',
              isSelected ? 'data-table__row--selected' : ''
            ]
              .filter(Boolean)
              .join(' ') || undefined;
            return (
              <tr key={id} onClick={() => onRowClick?.(row)} className={className}>
                {columns.map((column) => (
                  <td key={column.key}>{column.render(row)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
